<template>
  <div 
    v-if="ratio" 
    class="responsive-image-container"
    :style="{ aspectRatio: ratio }"
  >
    <picture v-if="image && primarySrc" class="responsive-picture">
      <source 
        v-if="optimizedSrcsets.avif"
        :srcset="optimizedSrcsets.avif"
        :sizes="responsiveSizes"
        type="image/avif"
      />
      <source 
        v-if="optimizedSrcsets.webp"
        :srcset="optimizedSrcsets.webp"
        :sizes="responsiveSizes"
        type="image/webp"
      />
      <source 
        v-if="optimizedSrcsets.fallback"
        :srcset="optimizedSrcsets.fallback"
        :sizes="responsiveSizes"
        type="image/jpeg"
      />
      <img
        ref="imgEl"
        :src="primarySrc"
        :srcset="primarySrcset"
        :sizes="responsiveSizes"
        :alt="image.alt || ''"
        :class="imageClass"
        @error="handleImageError"
        @load="handleImageLoad"
      />
    </picture>
    <!-- Preview image shown while loading -->
    <img 
      v-if="!isLoaded && previewSrc"
      :src="previewSrc"
      :alt="image.alt || ''"
      class="responsive-image-preview"
    />
  </div>
  <picture v-else-if="image && primarySrc" class="responsive-picture">
    <source 
      v-if="optimizedSrcsets.avif"
      :srcset="optimizedSrcsets.avif"
      :sizes="responsiveSizes"
      type="image/avif"
    />
    <source 
      v-if="optimizedSrcsets.webp"
      :srcset="optimizedSrcsets.webp"
      :sizes="responsiveSizes"
      type="image/webp"
    />
    <source 
      v-if="optimizedSrcsets.fallback"
      :srcset="optimizedSrcsets.fallback"
      :sizes="responsiveSizes"
      type="image/jpeg"
    />
    <img
      ref="imgEl"
      :src="primarySrc"
      :srcset="primarySrcset"
      :sizes="responsiveSizes"
      :alt="image.alt || ''"
      :class="imageClass"
      @error="handleImageError"
      @load="handleImageLoad"
    />
  </picture>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue'
import { useStrapiStore } from '../stores/strapi'
import { makeSrcset } from '../stores/strapi/config'
import { getSizesFromPreset, type SizePreset } from '../utils/responsive-breakpoints'
import { 
  isCloudinaryUrl, 
  createOptimizedSrcset, 
  OPTIMIZATION_PRESETS,
  generateOptimizedCloudinaryUrl 
} from '../utils/cloudinary-optimization'

interface Props {
  image: any
  sizes?: string
  class?: string
  lazy?: boolean
  containerSize?: SizePreset | any
  optimisationPreset?: 'hero' | 'content' | 'thumbnail' | 'efficient'
  ratio?: string
}

const props = withDefaults(defineProps<Props>(), {
  sizes: '',
  class: '',
  lazy: true,
  containerSize: 'medium',
  optimisationPreset: 'content',
  ratio: undefined
})

const store = useStrapiStore()
const imgEl = ref<HTMLImageElement>()
const cloudinaryRetryCount = ref(0)
const isLoaded = ref(false)
const maxCloudinaryRetries = 2

const primarySrc = computed(() => {
  // Safety check for image object
  if (!props.image) {
    return ''
  }
  
  const originalUrl = store.getImageUrl(props.image)
  
  // Apply Cloudinary optimisations if it's a Cloudinary URL
  // This will automatically include f_auto and dpr_auto
  if (originalUrl && isCloudinaryUrl(originalUrl)) {
    return generateOptimizedCloudinaryUrl(originalUrl, {
      ...OPTIMIZATION_PRESETS[props.optimisationPreset],
      width: 1200 // Reasonable default width for the primary source
    })
  }
  
  return originalUrl
})

const previewSrc = computed(() => {
  // Generate a small preview image (100px wide) for loading state
  if (!props.image || !props.ratio) {
    return ''
  }
  
  const originalUrl = store.getImageUrl(props.image)
  
  if (!originalUrl) {
    return ''
  }
  
  if (isCloudinaryUrl(originalUrl)) {
    const optimizedUrl = generateOptimizedCloudinaryUrl(originalUrl, {
      width: 100,
      quality: 30,
      format: 'webp'
    })
    return optimizedUrl || originalUrl
  }
  
  return originalUrl
})

const primarySrcset = computed(() => {
  // Safety check for image object
  if (!props.image) {
    return ''
  }

  // Use Cloudinary responsive srcset if available (with f_auto, dpr_auto)
  if (props.image.cloudSrcset) {
    return props.image.cloudSrcset
  }
  
  // Fallback to legacy formats
  if (props.image.formats) {
    return makeSrcset(props.image.formats)
  }
  if (props.image.srcset) {
    return props.image.srcset
  }
  return ''
})

const generateAdvancedSrcset = (formats: any): string => {
  if (!formats || typeof formats !== 'object') return ''
  const srcsetItems: string[] = []
  const hasBreakpointNames = Object.values(formats).some((format: any) => format?.breakpoint)

  if (hasBreakpointNames) {
    const breakpointGroups: { [key: string]: any[] } = {}
    Object.values(formats).forEach((format: any) => {
      if (format?.url && format?.width && format?.breakpoint) {
        const base = format.breakpoint.replace('-1x', '').replace('-2x', '')
        if (!breakpointGroups[base]) {
          breakpointGroups[base] = []
        }
        breakpointGroups[base].push(format)
      }
    })
    Object.entries(breakpointGroups).forEach(([_, list]) => {
      const oneX = list.find(f => f.breakpoint.endsWith('-1x'))
      const twoX = list.find(f => f.breakpoint.endsWith('-2x'))
      if (oneX) {
        const url1 = oneX.url.startsWith('http')
          ? oneX.url
          : `${store.getImageUrl({ url: oneX.url })}`
        srcsetItems.push(`${url1} ${oneX.width}w`)
        if (twoX) {
          const url2 = twoX.url.startsWith('http')
            ? twoX.url
            : `${store.getImageUrl({ url: twoX.url })}`
          srcsetItems.push(`${url2} ${twoX.width}w`)
        }
      }
    })
  } else {
    return makeSrcset(formats)
  }

  return srcsetItems.join(', ')
}

const responsiveSizes = computed(() => {
  if (props.sizes) {
    return props.sizes
  }
  
  // Handle Strapi size object format
  if (props.containerSize && typeof props.containerSize === 'object') {
    // Convert Strapi size enum to our preset
    const strapiSize = props.containerSize.size
    const sizeMapping: Record<string, SizePreset> = {
      'Full': 'full',
      'Contained': 'large',
      'Contained_Small': 'medium',
      'Contained_Small_Centred': 'medium',
      'Narrow': 'small',
      'Narrow_Centred': 'small',
      'Two_Third': 'medium',
      'Half': 'small',
      'Third': 'small',
      'Default': 'medium'
    }
    
    const preset = sizeMapping[strapiSize] || 'medium'
    return getSizesFromPreset(preset)
  }
  
  // Handle string preset format
  if (typeof props.containerSize === 'string') {
    return getSizesFromPreset(props.containerSize as SizePreset)
  }
  
  // Fallback
  return getSizesFromPreset('medium')
})

// Generate optimised srcsets for different formats leveraging Cloudinary's f_auto
const optimizedSrcsets = computed(() => {
  if (!props.image?.url || !isCloudinaryUrl(props.image.url)) {
    return {
      avif: null,
      webp: null, 
      fallback: null
    }
  }

  // Use breakpoint-aware sizes for better optimization
  const sizes = [480, 768, 1024, 1200, 1439, 1619, 1919]
  const optimisationOptions = OPTIMIZATION_PRESETS[props.optimisationPreset]

  return {
    // AVIF - best compression for modern browsers
    avif: createOptimizedSrcset(props.image.url, sizes, { 
      ...optimisationOptions, 
      format: 'avif'
    }),
    // WebP - great compression with wider browser support
    webp: createOptimizedSrcset(props.image.url, sizes, { 
      ...optimisationOptions, 
      format: 'webp' 
    }),
    // Auto format fallback (will use JPEG/PNG as appropriate)
    fallback: createOptimizedSrcset(props.image.url, sizes, { 
      ...optimisationOptions, 
      format: 'auto' 
    })
  }
})

const imageClass = computed(() => {
  const classes = ['responsive-image']
  if (props.class) classes.push(props.class)
  if (isLoaded.value) classes.push('loaded')
  return classes.join(' ')
})

const handleImageError = async () => {
  if (cloudinaryRetryCount.value < maxCloudinaryRetries) {
    cloudinaryRetryCount.value++
    await nextTick()
    if (imgEl.value) {
      imgEl.value.src = primarySrc.value ?? ''
    }
  }
  // For Cloudinary-only setup with f_auto and dpr_auto, we rely on Cloudinary's reliability
  // rather than local fallbacks
}

const handleImageLoad = () => {
  isLoaded.value = true
}

onMounted(() => {
  if (props.lazy && imgEl.value) {
    imgEl.value.loading = 'lazy'
  }
})
</script>

<style scoped>
.responsive-image-container {
  position: relative;
  width: 100%;
  overflow: hidden;
}

.responsive-image-container .responsive-picture {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.responsive-image-container .responsive-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.responsive-image-preview {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: blur(5px);
  opacity: 0.8;
  z-index: 1;
}

.responsive-picture {
  display: block;
  width: 100%;
  height: auto;
}

.responsive-image {
  max-width: 100%;
  height: auto;
  opacity: 0;
  transition: opacity 0.3s ease, filter 0.2s ease;
  position: relative;
  display: block;
  z-index: 2;
}

.responsive-image.loaded {
  opacity: 1;
}

/* Loading shimmer animation */
@keyframes shimmer {
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
}

.responsive-image:not(.loaded) {
  background: linear-gradient(90deg, #f0f0f0 25%, transparent 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: shimmer 1.5s infinite;
}

/* Ensure picture element inherits styles properly */
.responsive-picture img {
  width: 100%;
  height: auto;
  object-fit: cover;
}

/* Optimize for different connection speeds */
@media (prefers-reduced-data: reduce) {
  .responsive-image {
    /* For users who prefer reduced data usage */
    filter: contrast(1.1) brightness(1.05);
  }
}

/* Hide the indicator on mobile to reduce clutter */
@media (max-width: 768px) {
  .responsive-picture::before {
    display: none;
  }
}
</style>
